import { AppStore } from "../../../stores/app-store.service";
import { Observable, of, Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatRowDef,
  MatRow
} from "@angular/material/table";
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import { MatTooltip } from "@angular/material/tooltip";
import { SvgIconComponent } from "angular-svg-icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatRadioGroup, MatRadioButton } from "@angular/material/radio";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { InfiniteScrollDirective } from "../../../directives/infinite-scroll.directive";
import { DatePickerComponent } from "../date-picker/date-picker.component";
import { FilterButtonFooterComponent } from "./filter-button-footer/filter-button-footer.component";
import { NotificationService } from "../../../services/notification.service";
import {
  FiltersControllerService,
  FiltersOptions,
  FiltersData,
  FilterInputData
} from "../../../services/filters.service";
import { FilterStatus } from "../../../models/filters";
import {
  ColorStatusRow,
  applyColorStatusSelection,
  buildColorStatusRows,
  computeColorStatusActive,
  markerColorClass,
  refreshColorStatusRows,
  toggleColorStatusRow,
  uncheckColorStatusRows
} from "../../../models/color-status-filter";
import { IncidentDialogComponent } from "../../pages/incident/incident-dialog/incident-dialog.component";
import { Range } from "../../../models/range-status-days";

/**
 * Which chrome a filter wears — and, with it, when it applies.
 *
 * `menu` is the header dropdown: a button opening a `mat-menu` that stages
 * every change until Apply or menu-close. `panel` is the sidenav accordion: an
 * expansion panel that emits each change as it is made. `detail` is the
 * sidenav's drill-down view — the whole sidebar body, one filter, no header of
 * its own because the sidenav draws it — and applies live like `panel` does.
 * Nothing else about a filter depends on which one it wears, which is why they
 * are one input and not a chrome plus an apply mode nobody would ever mix.
 */
export type FilterChrome = "menu" | "panel" | "detail";

/**
 * One filter control, three chromes.
 *
 * The selector answers to both `<app-filter-button>` (chrome `menu`, the
 * default) and `<app-filter-panel>` (which passes `chrome="panel"` or
 * `"detail"`), so every host site still reads as the thing it puts on screen.
 * Each control type — `inputData`, `checkbox`, `label`, `radio`, `dateRange`,
 * `range`, `colorStatus` — is written once, in the `#controls` template.
 */
@Component({
  selector: "app-filter-button, app-filter-panel",
  templateUrl: "./filter-control.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [
    "./filter-control.shared.scss",
    "./filter-control.menu.scss",
    "./filter-control.panel.scss",
    "./filter-control.detail.scss"
  ],
  imports: [
    NgClass,
    NgTemplateOutlet,
    MatTooltip,
    SvgIconComponent,
    MatMenu,
    MatMenuTrigger,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatProgressSpinner,
    MatIcon,
    FormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSort,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatRowDef,
    MatRow,
    InfiniteScrollDirective,
    MatRadioGroup,
    MatRadioButton,
    DatePickerComponent,
    MatFormField,
    MatInput,
    FilterButtonFooterComponent,
    TranslateModule
  ]
})
export class FilterControlComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Output() manageList: EventEmitter<any> = new EventEmitter();
  @Output() displayMore: EventEmitter<void> = new EventEmitter();

  @Input() chrome: FilterChrome = "menu";
  /** The sidenav's short, static lists (radius, devicesTypes) have nothing to search. */
  @Input() searchable: boolean = true;

  @Input() pathTranslate: string;
  @Input() type: string;
  @Input() icon: string;
  @Input() label: string;
  @Input() maxItems: number;
  @Input() isEmpty: boolean = false;
  @Input() iconOnly: boolean = false;
  @Input() showType: string = "show";
  @Input() defaultRadioValue: string;
  @Input() isLoading: boolean = false;
  @Input()
  set checkedData(value: Array<string | number>) {
    // A parent may bind this to undefined/null; keep it an array so template
    // and component logic can always rely on `.length` / `.includes`.
    this._checkedData = value ?? [];
  }
  get checkedData(): Array<string | number> {
    return this._checkedData;
  }
  private _checkedData: Array<string | number> = [];
  @Input() rangeData?: Range | undefined;
  /** Panel chrome only: start the accordion open. */
  @Input() expanded: boolean = false;

  @Input() radioData: readonly string[];
  @Input() dateRangeData: Date[];
  @Input() inputData: FilterInputData;
  @Input() colorStatusData: FilterStatus[];
  @Input() dataOverride: FiltersData[];
  @Input() showAllVariant: boolean = false;

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild("search", { static: false }) inputElement: ElementRef;

  public rangeValues: Range = {};
  private skip: number = 0;
  private limit: number = 100;
  private debounceTime: number = 400;
  private currentRequestSub?: Subscription;
  private filterMethodsMap!: {
    [key: string]: (filters: FiltersOptions) => Observable<FiltersData[]>;
  };

  private readonly deviceIdsPrefix: string[] = ["SFX_", "SNT_", "N5_"];

  public data: FiltersData[] = [];
  public dataLoading: boolean = false;
  public dataFetched: boolean = false;
  public allDataFetched: boolean = false;

  public radioItem: string;
  public activeData: number = 0;
  public callApply: boolean = false;
  public allFiltersActive: boolean = false;
  public dataSource = new MatTableDataSource([]);
  public matchDataSource = new MatTableDataSource([]);
  public displayedColumns: string[] = ["checkedItem", "key"];
  public markerColorClass = markerColorClass;

  public searchValue: string = "";
  public searchValueUpdate = new Subject<string>();

  constructor(
    public dialog: MatDialog,
    private store: AppStore,
    private translate: TranslateService,
    private notifierService: NotificationService,
    private filtersControllerService: FiltersControllerService,
    private cdr: ChangeDetectorRef
  ) {
    this.searchValueUpdate
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe(() => {
        this.resetData();
        this.getData();
      });
  }

  /**
   * The one behavioural difference between the chromes: both sidenav chromes
   * emit each change as it happens, the header dropdown stages it until Apply.
   * `range` and `dateRange` stage under all three — they are typed into, not
   * clicked.
   */
  private get appliesImmediately(): boolean {
    return this.chrome !== "menu";
  }

  public ngOnInit(): void {
    // colorStatus counts active statuses from colorStatusData, not checkedData.
    // ngOnInit runs after ngOnChanges, so read it the same way here rather than
    // wiping the badge count back to 0 on load/refresh.
    if (this.type === "colorStatus") {
      this.updateColorStatusActiveData();
    } else if (this.type === "radio") {
      // A radio always has exactly one option selected (its filter declares a
      // base value), so its badge is always 1 — never a count of checkedData.
      this.activeData = 1;
    } else {
      this.activeData = this.checkedData?.length ?? 0;
    }
    // Initialize fetch data callback functions
    this.filterMethodsMap = {
      containerType: this.filtersControllerService.getFiltersContainers.bind(
        this.filtersControllerService
      ),
      device: this.filtersControllerService.getFiltersDevices.bind(
        this.filtersControllerService
      ),
      lastAuthorizedSite: this.filtersControllerService.getFiltersSites.bind(
        this.filtersControllerService
      ),
      sites: this.filtersControllerService.getFiltersSites.bind(
        this.filtersControllerService
      ),
      departureSites: this.filtersControllerService.getFiltersSites.bind(
        this.filtersControllerService
      ),
      arrivalSites: this.filtersControllerService.getFiltersSites.bind(
        this.filtersControllerService
      ),
      devicesTypes: () =>
        of(
          this.filtersControllerService.getFilterDevicesTypes({
            skip: this.skip,
            limit: this.limit,
            search: this.searchValue
          })
        ),
      radius: () =>
        of(
          this.filtersControllerService.getFilterRadius({
            skip: this.skip,
            limit: this.limit,
            search: this.searchValue
          })
        ),
      incident: this.filtersControllerService.getFilterIncidentsOpened.bind(
        this.filtersControllerService
      ),
      addIncident: this.filtersControllerService.getFilterIncidents.bind(
        this.filtersControllerService
      ),
      alertType: this.filtersControllerService.getFilterAlertTypes.bind(
        this.filtersControllerService
      ),
      containerCategory: () =>
        of(
          this.filtersControllerService.getFilterContainerCategories({
            skip: this.skip,
            limit: this.limit,
            search: this.searchValue
          })
        )
    };

    if (this.type === "range") {
      this.rangeValues = this.rangeData ?? {};
      this.activeData = this.rangeActive(this.rangeValues);
    } else if (this.type === "dateRange") {
      this.activeData = this.datesActive(this.dateRangeData);
    }

    // The drill-down view has no open event — it is created when its row is
    // clicked and destroyed on the way back — so it loads its first page here.
    // Doing it now rather than in ngAfterViewInit keeps the fetch's first
    // synchronous state change ahead of the initial render.
    if (this.chrome === "detail") {
      this.opened();
    }
  }

  /**
   * A range filter's badge: the filter is ON as soon as ONE bound is set.
   * Half-open is a normal state ("from 5 days" / "up to 5 days") since the
   * 0/361 auto-fill was dropped, so requiring both bounds made a filled field
   * read as switched off. Both the badge and the blue highlight come from this.
   */
  private rangeActive(range: Range | undefined): number {
    return range?.start !== undefined || range?.end !== undefined ? 1 : 0;
  }

  /** Same rule for the two date pickers: one date set is enough. */
  private datesActive(dates: Date[] | undefined): number {
    return dates?.some((date) => !!date) ? 1 : 0;
  }

  public ngAfterViewInit(): void {
    // `trigger` is only resolved once the view is initialised, but the footer
    // binds it through [menuTrigger]. Re-run change detection so the resolved
    // value is picked up in the same tick (avoids NG0100). The sidenav chromes
    // have neither a trigger nor a footer, so they have nothing to re-run.
    if (!this.appliesImmediately) {
      this.cdr.detectChanges();
    }

    // Detail chrome opened itself in ngOnInit, before the search input existed.
    if (this.chrome === "detail") {
      this.searchBarFocus();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update activeData when checkedData changes
    if (changes.checkedData && this.type === "checkbox") {
      this.activeData = this.checkedData?.length ?? 0;
    }

    // If dataOverride changes, update the data source
    if (changes.dataOverride && changes.dataOverride.currentValue) {
      this.allDataFetched = true;
      this.data = []; // Clear existing data first
      this.updateDataSource(changes.dataOverride.currentValue);
    }

    if (changes.maxItems && this.maxItems) {
      if (this.checkedData.length > this.maxItems) {
        this.clearCheckbox();
      }
    }

    if (
      changes.colorStatusData &&
      this.colorStatusData &&
      this.type === "colorStatus"
    ) {
      // Build the checkbox rows from colorStatusData, or refresh their checked
      // state when the store updates. Guarded on colorStatusData actually
      // changing so unrelated change detection can't discard a staged toggle
      // while the menu is still open.
      this.data =
        this.data.length === 0
          ? buildColorStatusRows(this.colorStatusData, this.showType)
          : refreshColorStatusRows(
              this.data as ColorStatusRow[],
              this.colorStatusData,
              this.showType
            );
      this.dataSource.data = this.data;

      this.updateColorStatusActiveData();
    }

    // The selected option is owned by the parent: follow it.
    if (changes.defaultRadioValue) {
      this.radioItem = changes.defaultRadioValue.currentValue;
    }

    // The range/date fields are owned by the parent as well: a value landing
    // after creation (URL hydration, a sidebar Apply) or a Clear All wiping it
    // has to move the badge with it. An in-place edit from manageRange shares
    // the object with the parent, so it never reaches here (Angular compares by
    // reference) and staged input can't be clobbered.
    if (changes.rangeData && this.type === "range") {
      this.rangeValues = this.rangeData ?? {};
      this.activeData = this.rangeActive(this.rangeValues);
    }

    if (changes.dateRangeData && this.type === "dateRange") {
      this.activeData = this.datesActive(this.dateRangeData);
    }
  }

  public ngOnDestroy(): void {
    this.currentRequestSub?.unsubscribe();
  }

  public searchBarFocus(): void {
    if (
      !this.isEmpty &&
      this.inputElement &&
      this.type !== "dateRange" &&
      this.type !== "range"
    ) {
      this.inputElement.nativeElement.focus();
    }
  }

  /**
   * Any chrome, on open: a checkbox list loads its first page once. The chromes
   * used to disagree here — the dropdown asked `type`, the sidenav kept an
   * allowlist of three labels — because the sidenav had no server-paged checkbox
   * to load. Now that it does, the label list would have to grow with every
   * filter, so they all ask `type` instead. `colorStatus` is fed from an
   * `@Input`, so it has no fetch method and never needed naming here. `detail`
   * has no open event at all and calls this from `ngOnInit`.
   */
  public opened(): void {
    if (!this.data.length && this.type === "checkbox") {
      this.getData();
    }

    this.searchBarFocus();
  }

  public updateMatchDataSource(): void {
    const search: string = this.searchValue.toUpperCase();

    this.matchDataSource.data = this.data.filter(({ key }) => {
      const keyStr: string = String(key).toUpperCase();

      // deviceIds filter handling
      const prefix = this.deviceIdsPrefix.find((p) => keyStr.startsWith(p));
      if (prefix) {
        return search.includes(keyStr.slice(prefix.length));
      }

      return keyStr === search;
    });
  }

  public updateDataSource(filtersData: FiltersData[]): void {
    filtersData = filtersData.map((filterData) => {
      return {
        ...filterData,
        checked: this.checkedData?.includes(filterData.key) ?? false
      };
    });
    this.data = [...this.data, ...filtersData];
    this.dataSource.data = this.data;
    if (this.searchValue) {
      this.updateMatchDataSource();
    }
    this.skip += this.limit;
    this.dataLoading = false;
    this.dataFetched = true;
  }

  public getData(): void {
    if (this.currentRequestSub) {
      this.currentRequestSub.unsubscribe();
    }

    if (this.dataOverride) {
      this.dataLoading = true;
      this.allDataFetched = true;
      // dataOverride is the whole list at once: search it here, since there is
      // no request to hand the term to.
      let filteredData: FiltersData[] = this.dataOverride;
      if (this.searchValue) {
        const searchUpper: string = this.searchValue.toUpperCase();
        filteredData = this.dataOverride.filter((item) => {
          const keyStr: string = String(item.key).toUpperCase();
          const displayedStr: string = String(
            item.keyDisplayed || item.key
          ).toUpperCase();
          return (
            keyStr.includes(searchUpper) || displayedStr.includes(searchUpper)
          );
        });
      }
      this.updateDataSource(filteredData);
      return;
    }

    // Some filters (colorStatus) are fed from an @Input, not the API, so they
    // have no fetch method — nothing to load, and no spinner to raise.
    const fetchMethod = this.filterMethodsMap?.[this.label];
    if (!fetchMethod) {
      return;
    }

    this.dataLoading = true;
    this.currentRequestSub = fetchMethod({
      skip: this.skip,
      limit: this.limit,
      search: this.searchValue
    }).subscribe((filtersData: FiltersData[]) => {
      this.updateDataSource(filtersData);
      if (filtersData?.length < this.limit) {
        this.allDataFetched = true;
      }
    });
  }

  public manageItem(item, event?): void {
    event?.preventDefault?.();

    if (this.maxItems <= this.checkedData.length && !item.checked) {
      let errorMsg: string = "limit items exceed";

      this.translate
        .get("filter.buttonListError", {
          maxItems: this.maxItems
        })
        .subscribe((message) => {
          errorMsg = message;
        });

      this.notifierService.notify(errorMsg);
      return;
    }

    const index: number = this.data.findIndex((e) => e.key === item.key);
    if (index < 0) return;

    this.callApply = true;
    const checked: boolean = !this.data[index].checked;
    this.data[index].checked = checked;

    if (this.appliesImmediately) {
      // Hand the parent a new array so its binding actually changes, then emit.
      this.checkedData = checked
        ? [...this.checkedData, item.key]
        : this.checkedData.filter((key) => key !== item.key);
      this.applyFilter();
    } else {
      // Staged: edit the array the parent passed in (the setter kept the
      // reference) and emit nothing until Apply / menu close.
      if (checked) {
        this.checkedData.push(item.key);
      } else {
        this.checkedData.splice(
          this.checkedData.findIndex((key) => key === item.key),
          1
        );
      }
    }
  }

  public manageItemRadio(item): void {
    this.callApply = true;
    this.radioItem = item.value;

    if (this.appliesImmediately) {
      this.applyRadio();
    }
  }

  public manageDates(dateType: string, date: Date): void {
    this.callApply = true;
    this.dateRangeData[dateType === "start" ? 0 : 1] = date;
  }

  public manageRange(type: "start" | "end", event: Event): void {
    this.callApply = true;

    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.valueAsNumber;

    value = isNaN(value) ? undefined : Math.min(Math.max(value, 0), 361);

    this.rangeValues[type] = value;

    // Keep the field showing what we clamped it to (and handle input = "").
    if (value !== undefined && inputElement.value !== value.toString()) {
      inputElement.value = value.toString();
    }

    this.activeData =
      this.rangeValues.start === undefined && this.rangeValues.end === undefined
        ? 0
        : 1;
    if (this.activeData === 0) {
      this.rangeValues = {};
    }
  }

  public manageFilter(item) {
    this.manageList.emit(item);
  }

  public manageColorStatusItem(item: any, event?: any): void {
    event?.preventDefault?.();

    if (!this.data.some((row: any) => row.marker === item.marker)) return;

    this.callApply = true;
    this.data = toggleColorStatusRow(
      this.data as ColorStatusRow[],
      item.marker
    );
    this.dataSource.data = [...this.data];

    if (this.appliesImmediately) {
      this.applyColorStatus();
    }
  }

  private updateColorStatusActiveData(): void {
    const { allFiltersActive, activeData } = computeColorStatusActive(
      this.colorStatusData,
      this.showType
    );
    this.allFiltersActive = allFiltersActive;
    this.activeData = activeData;
  }

  private applyColorStatus(): void {
    this.callApply = false;

    const updated = applyColorStatusSelection(
      this.colorStatusData,
      this.data as ColorStatusRow[],
      this.showType
    );
    this.manageList.emit(updated);

    // The badge follows the selection we just emitted. The store echoes the
    // same list back through colorStatusData, so this only makes the count
    // arrive with the click instead of one tick later.
    const { allFiltersActive, activeData } = computeColorStatusActive(
      updated,
      this.showType
    );
    this.allFiltersActive = allFiltersActive;
    this.activeData = activeData;
  }

  public loadMore = () => {
    if (!this.dataLoading && !this.allDataFetched) this.getData();
  };

  public resetData(): void {
    this.skip = 0;
    this.data = [];
    this.matchDataSource.data = [];
    this.dataFetched = false;
    this.allDataFetched = false;
  }

  private applyRadio(): void {
    this.activeData = 1;
    this.callApply = false;
    this.manageList.emit(this.radioItem);
  }

  private applyDates(): void {
    this.callApply = false;
    this.activeData = this.datesActive(this.dateRangeData);
    this.manageList.emit(this.dateRangeData);
  }

  private applyRange(): void {
    this.callApply = false;

    // A half-open range is a normal use, not something to complete: filling only
    // one field means "from 5 days" or "up to 5 days" and is left exactly as
    // typed, so it round-trips through the URL as `5-` / `-5`. (The 0/361
    // auto-fill that used to sit here overwrote the empty field.)
    if (
      this.rangeValues.start !== undefined &&
      this.rangeValues.end !== undefined &&
      this.rangeValues.start > this.rangeValues.end
    ) {
      this.rangeValues = {
        start: this.rangeValues.end,
        end: this.rangeValues.start
      };
    }

    this.manageList.emit(this.rangeValues);

    this.activeData = this.rangeActive(this.rangeValues);
  }

  private applyFilter(): void {
    this.activeData = this.checkedData.length;
    this.callApply = false;
    this.manageList.emit(this.checkedData);
    if (this.label === "addIncident" && !this.checkedData?.length) {
      // Panel chrome has no menu to close.
      this.trigger?.closeMenu();
    }
  }

  public applyItems(forceApply?: boolean): void {
    if (this.callApply || forceApply) {
      switch (this.type) {
        case "radio":
          this.applyRadio();
          return;
        case "dateRange":
          this.applyDates();
          return;
        case "range":
          this.applyRange();
          return;
        case "colorStatus":
          this.applyColorStatus();
          return;
        default:
          this.applyFilter();
          return;
      }
    }
  }

  private clearFilters(): void {
    this.resetColorStatusLocalData();
    this.store.inactiveStatus();
  }

  private clearFiltersDetail(): void {
    this.resetColorStatusLocalData();
    this.store.inactiveStatusDetail();
  }

  // Clear = uncheck every status (empty selection). Like the other checkbox
  // filters, an empty selection means "no filter" (everything is shown). Also
  // drops the staged state so a pending toggle can't be re-applied on the
  // menu-close that follows a clear.
  private resetColorStatusLocalData(): void {
    this.callApply = false;
    this.data = uncheckColorStatusRows(this.data as ColorStatusRow[]);
    this.dataSource.data = this.data;
  }

  private clearDateRange(): void {
    this.callApply = false;
    this.activeData = 0;
    this.manageList.emit(undefined);
  }

  private clearCheckbox(): void {
    this.data = this.data.map((item) => {
      return { ...item, checked: false };
    });
    this.updateMatchDataSource();

    this.checkedData = [];
    this.dataSource.data = this.data;
    if (this.activeData && this.label !== "addIncident") {
      this.callApply = true;
      this.applyItems();
    }
  }

  clearRange(): void {
    this.rangeValues = {};
    this.callApply = false;
    this.activeData = 0;
    this.manageList.emit(this.rangeValues);
  }

  /** No "radio" case: a radio has no empty state, so Clear is never shown. */
  public clearItems(): void {
    switch (this.type) {
      case "checkbox":
        this.clearCheckbox();
        return;
      case "dateRange":
        this.clearDateRange();
        return;
      case "colorStatus":
        this.showType === "show"
          ? this.clearFilters()
          : this.clearFiltersDetail();
        return;
      case "range":
        this.clearRange();
        return;
      default:
        return;
    }
  }

  public applyClosed(): void {
    if (this.label !== "addIncident") {
      this.applyItems();
    }
  }

  public openDialog() {
    const dialogRef = this.dialog.open(IncidentDialogComponent, {
      maxWidth: "90vw",
      minHeight: "85vh",
      width: "90vw",
      height: "85vh",
      data: {
        incidents: this.data,
        checkedData: this.checkedData,
        label: this.label
      }
    });

    if (!this.isEmpty) {
      dialogRef.componentInstance.manageIncident.subscribe((incident) => {
        this.manageItem(incident);
      });
    }

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "incidentApply") {
        if (this.label === "addIncident" && !this.checkedData?.length) {
          this.applyItems(true);
        } else {
          this.applyItems();
        }
        this.trigger?.closeMenu();
      }
    });
  }

  public ondisplayMore(): void {
    this.displayMore.emit();
    this.openDialog();
  }

  public interpolatePathTranslate(item: string): string {
    return this.pathTranslate
      ? this.pathTranslate.replace("{{item}}", item)
      : item;
  }

  /** Panel chrome only: what a collapsed header says under its title. */
  public getSummaryText(): string {
    if (!this.checkedData?.length) return "";

    // Walk the selection, not the loaded rows: a collapsed panel fetches on its
    // first open, so a filter restored from the URL has a selection long before
    // it has any rows to look a display name up in. The key is the fallback.
    const labels: string[] = this.checkedData.map((key) => {
      const row = this.data.find((item) => item.key === key);
      // A locally-built row carries its label as an i18n key, so resolve it here
      // the way the expanded rows resolve it through the pipe. `instant` is safe
      // at this point: a collapsed header is only ever rendered after the panel
      // has been opened once, long after the catalogs have loaded.
      return String(
        row?.labelKey
          ? this.translate.instant(row.labelKey, row.labelParams)
          : (row?.keyDisplayed ?? key)
      );
    });

    return labels.length <= 3
      ? labels.join(", ")
      : `${labels.slice(0, 3).join(", ")}...`;
  }
}
